import { CalendarDays } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { Initial } from '@/components/paper'
import {
  comingUp,
  entryLink,
  entryTitle,
  useCircleCalendar,
  useEveryoneCalendar,
  whenLabel,
  type CalendarEntry,
} from '@/lib/calendar'
import { firstName } from '@/lib/tree'
import { cn } from '@/lib/utils'

// How far ahead the rings look, and how soon a day is near enough to ring in the logo's colours.
const AHEAD = 30
const SOON = 7

const daysUntil = (on: string, today: string) =>
  Math.round((Date.parse(`${on}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000)

// The Modern Calendar's rings: the next month of birthdays, remembrance days and added dates, soonest first, whichever month is
// showing below. A ring is in the logo's colours for the coming week; remembrance days are drawn quietly, in grey.
export function ComingUp({
  familyId,
  spaceId,
  everyone,
  today,
}: {
  familyId: string
  spaceId: string | undefined
  everyone: boolean
  today: string
}) {
  const circle = useCircleCalendar(familyId, spaceId, today, AHEAD, !everyone)
  const all = useEveryoneCalendar(today, AHEAD, everyone)
  const entries = ((everyone ? all.data : circle.data)?.entries ?? []).filter(comingUp)
  if (entries.length === 0) return null
  return (
    <section aria-labelledby="coming-up" className="flex flex-col gap-2">
      <h2 id="coming-up" className="eyebrow">
        Coming up
      </h2>
      <ul aria-label="Coming up" className="-mx-4 scroll-row gap-2 px-4 py-1 nav:-mx-2 nav:px-2">
        {entries.map((entry) => {
          if (entry.kind === 'on-this-day') return null
          const added = entry.kind === 'date'
          const remembered = entry.kind !== 'birthday' && !added
          const when = whenLabel(entry.on, today)
          return (
            <li
              key={`${entry.kind}:${added ? entry.date.id : entry.person.id}:${entry.on}`}
              className="shrink-0 snap-start"
            >
              <Link
                to={entryLink(entry, everyone ? undefined : familyId)!}
                className="mp-ring"
                aria-label={`${entryTitle(entry)}, ${when}`}
              >
                <span
                  className="mp-ring-o"
                  data-fresh={(!remembered && daysUntil(entry.on, today) < SOON) || undefined}
                >
                  <span>
                    {added ? (
                      <span className="grid size-full place-items-center rounded-full bg-secondary">
                        <CalendarDays className="size-8 stroke-[1.5]" aria-hidden />
                      </span>
                    ) : (
                      <Initial
                        name={entry.person.name}
                        portrait={entry.person.portrait}
                        className={cn('size-full text-2xl', remembered && 'grayscale')}
                      />
                    )}
                  </span>
                </span>
                <span className="w-full truncate text-xs font-medium">
                  {added ? entry.date.title : firstName(entry.person.name)}
                </span>
                <span className="-mt-1 w-full truncate text-xs text-muted-foreground">{when}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

// The month in the Modern Calendar: each day that has something on it as a card of its own, its date set large.
// Today's card is outlined; the days already gone this month step back.
export function DayCards({
  days,
  today,
  row,
}: {
  days: [string, CalendarEntry[]][]
  today: string
  row: (entry: CalendarEntry) => ReactNode
}) {
  return (
    <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-label="Days this month">
      {days.map(([day, entries]) => {
        const date = new Date(`${day}T00:00:00Z`)
        return (
          <li
            key={day}
            id={`day-${day}`}
            className={cn(
              'flex gap-4 rounded-[1.75rem] bg-secondary/60 p-4',
              day === today && 'ring-2 ring-foreground',
              day < today && 'opacity-60',
            )}
          >
            <div className="w-12 shrink-0 text-center leading-none" aria-hidden>
              <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })}
              </p>
              <p className="mt-1 text-3xl font-semibold tracking-tight tabular-nums">{date.getUTCDate()}</p>
            </div>
            <ul
              className="flex min-w-0 flex-1 flex-col gap-1"
              aria-label={date.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC',
              })}
            >
              {entries.map((entry, i) => (
                <li key={i}>{row(entry)}</li>
              ))}
            </ul>
          </li>
        )
      })}
    </ol>
  )
}
